import { Button } from '@repo/ui/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@repo/ui/components/ui/collapsible'
import { cn } from '@repo/ui/lib/utils'
import { ChevronDown, Menu, X } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import type React from 'react'
import { useState } from 'react'
import type { DropdownNavLink, NavAction, NavigationContent, NavLink } from './shared'
import { NavActionButton, NavButton, useIsBelowBreakpoint } from './shared'

export interface MobileMenuProps {
  isOpen: boolean
  onClose: () => void
  content: NavigationContent
  links?: NavLink[]
}

export const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose, content, links }) => {
  const { actions = [] } = content
  const menuLinks = links || content.links || []
  const [expandedDropdowns, setExpandedDropdowns] = useState<Set<string>>(new Set())

  const toggleDropdown = (label: string) => {
    setExpandedDropdowns((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(label)) {
        newSet.delete(label)
      } else {
        newSet.add(label)
      }
      return newSet
    })
  }

  const handleSelection =
    (handler?: (event?: React.MouseEvent<HTMLElement>) => void) =>
    (event?: React.MouseEvent<HTMLElement>) => {
      handler?.(event)
      if (!event || !event.defaultPrevented) {
        onClose()
      }
    }

  const renderNavLink = (link: NavLink) => {
    if (link.type === 'simple') {
      return (
        <NavButton
          key={link.label}
          label={link.label}
          icon={link.icon}
          href={link.href}
          target={link.target}
          onClick={handleSelection(link.onClick)}
          variant="ghost"
          buttonClassName="w-full justify-start px-1.5 py-2 text-base font-semibold text-foreground hover:text-foreground/80"
          childClassName="w-full justify-start"
        />
      )
    }

    if (link.type === 'dropdown') {
      return (
        <div key={link.label} className="border-b border-border/50">
          <Collapsible
            open={expandedDropdowns.has(link.label)}
            onOpenChange={() => toggleDropdown(link.label)}
          >
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                onClick={(e) => {
                  link.onClick?.(e)
                }}
                className="h-auto w-full justify-between px-0 py-2 text-left text-base font-semibold text-foreground hover:bg-accent/50"
              >
                {link.label}
                <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-1 pb-2 pl-4">
              {(link as DropdownNavLink).children.map((child) => (
                <NavButton
                  key={child.label}
                  label={child.label}
                  icon={child.icon}
                  href={child.href}
                  target={child.target}
                  onClick={handleSelection(child.onClick)}
                  variant="ghost"
                  buttonClassName="w-full justify-start px-1.5 py-2 text-base text-muted-foreground hover:bg-accent/30 hover:text-foreground"
                  childClassName="w-full justify-start"
                />
              ))}
            </CollapsibleContent>
          </Collapsible>
        </div>
      )
    }

    return null
  }

  const renderAction = (action: NavAction, index: number) => {
    return (
      <NavActionButton
        key={index}
        action={{
          ...action,
          onClick: handleSelection(action.onClick),
        }}
        className="w-full justify-center"
      />
    )
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[40] bg-background/20 backdrop-blur-xs"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{
              opacity: 1,
              y: 0,
              borderRadius: '20px',
              filter:
                'drop-shadow(0 24px 40px rgba(15, 23, 42, 0.35)) drop-shadow(0 0 0 rgba(15, 23, 42, 0))',
            }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
            className="fixed left-4 right-4 top-28 z-50 border border-border/20 bg-background/95 shadow-inner backdrop-blur-xl sm:left-6 sm:right-6"
            style={{ willChange: 'opacity, transform' }}
          >
            <div className="space-y-4 px-6 py-5">
              <div className="space-y-2">{menuLinks.map(renderNavLink)}</div>
              {actions.length > 0 && (
                <div className="space-y-2 pt-2">{actions.map(renderAction)}</div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export interface MobileToggleProps {
  isOpen: boolean
  onToggle: () => void
  className?: string
  mobileBreakpoint?: number
}

export const MobileToggle: React.FC<MobileToggleProps> = ({
  isOpen,
  onToggle,
  className,
  mobileBreakpoint = 768,
}) => {
  const isMobile = useIsBelowBreakpoint(mobileBreakpoint)

  if (!isMobile) {
    return null
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-expanded={isOpen}
      aria-label={isOpen ? 'Close navigation' : 'Open navigation'}
      className={cn(
        'relative z-10 rounded-full border border-border/60 bg-background/75 text-foreground shadow-sm hover:bg-muted/70',
        className
      )}
      onClick={onToggle}
    >
      {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
    </Button>
  )
}
